import React, { useCallback, useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';
import { Pressable, StyleSheet } from 'react-native';

import { IBackendCartEntries, IHistoricalOrder } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { Box, Typography } from 'components/ui';
import useErrorModal from 'hooks/use-error-modal';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import useOrderNumber from 'hooks/use-order-number';
import { t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';
import { useReorder } from 'state/order/hooks/use-reorder';
import { getDesignToken, shadows, spacing } from 'styles/constants';
import { useCreateOrderAriaLabel } from 'utils/accessibility/aria';
import { routes } from 'utils/routing';

import OrderAction from './order-action';
import { OrderPointsEarned } from './order-points-earned';
import { RewardText } from './reward-text';

interface IOrderProps {
  order: IHistoricalOrder;
}

const Order = ({ order }: IOrderProps) => {
  const isDesktop = useIsDesktopBreakpoint();
  const {
    reorder: { reorderedOrderId },
  } = useOrderContext();
  const { store, fetchStore } = useStoreContext();
  const { handleReorderClick, isReorderingDisabled } = useReorder(order);

  const router = useRouter();

  // @ts-ignore
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Fetch Store Failure',
  });
  const formattedDate = useMemo(
    () =>
      new Date(order.createdAt).toLocaleString(t('localeDateTime'), {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }),
    [order.createdAt]
  );

  const { itemCSV, numItems, rewardRedeemed } = useMemo(() => {
    const cartEntries = order.cart.cartEntries || [];
    const csvForItem = cartEntries.map((entry: IBackendCartEntries) => entry.name).join(', ');
    const totalItemsOrdered = cartEntries.reduce(
      (acc: number, item: IBackendCartEntries) => acc + item.quantity,
      0
    );

    const itemsCount = `${totalItemsOrdered} ${(totalItemsOrdered === 1
      ? t('item')
      : t('items')
    ).toLowerCase()}`;

    // order redeemed a reward if an entry has `redeemable` as true
    const wasRewardRedeemed = cartEntries.some((entry: IBackendCartEntries) => entry.redeemable);

    return { itemCSV: csvForItem, numItems: itemsCount, rewardRedeemed: wasRewardRedeemed };
  }, [order]);

  const showOrderModal = useCallback(() => {
    router.navigate(routes.orderDetails(order.rbiOrderId));
  }, [order.rbiOrderId, router]);

  const handleFinishOrder = async () => {
    const orderStoreId = order.cart?.storeId ? order.cart.storeId.toString() : '';

    // Store needs to be re-fetched if the current one is different is missing.
    if (store.number && store.number !== orderStoreId) {
      try {
        await fetchStore(orderStoreId);
      } catch {
        return openErrorDialog({ message: t('couldNotLocateStore') });
      }
    }
    router.replace(`${routes.orderConfirmation}/${order.rbiOrderId}`);
  };

  // Show the first item and tell the user how many other items there is in the order
  const itemsArr = itemCSV.split(',');
  const remainingItems = itemsArr.length - 1;
  const renderMoreItems = useCallback(
    () => (
      <Pressable onPress={() => showOrderModal()} style={styles.moreItems}>
        <Typography fontSize="sm" style={styles.moreItemsText}>
          {t('moreItems', { remainingItems })}
        </Typography>
      </Pressable>
    ),
    [remainingItems, showOrderModal]
  );

  const createAriaLabel = useCreateOrderAriaLabel();
  const orderNumber = useOrderNumber(order);

  const showTrackOrderModalPage = () =>
    router.navigate(`${routes.orderConfirmation}/${order.rbiOrderId}?dismissOnly=true`);

  return (
    <>
      <Box
        testID={`recent-order-${order.rbiOrderId}`}
        style={[styles.tile, isDesktop && styles.tileDesktop]}
      >
        <Box style={[styles.col, isDesktop && styles.colDesktop]} focusable>
          <Box style={styles.orderNumberWrapper}>
            {orderNumber && (
              <>
                <Typography variant="headerTwo" style={styles.orderNumberText}>
                  {`${t('orderNumber')}:`}
                </Typography>
                <Typography variant="headerTwo" style={styles.orderNumber}>
                  {orderNumber}
                </Typography>
              </>
            )}
          </Box>
          <Typography variant="headerTwo" style={styles.formattedDate}>
            {formattedDate}
          </Typography>
          <Box style={styles.subtitleRow}>
            <Typography variant="headerThree" style={styles.numItems}>
              {numItems}
            </Typography>
            {order.loyaltyTransaction && (
              <OrderPointsEarned loyaltyTransaction={order.loyaltyTransaction} />
            )}
          </Box>
          <Box style={styles.itemNameContainer}>
            {rewardRedeemed && <RewardText text={t('rewards')} />}
            <Box style={styles.items}>
              <Typography>{itemsArr[0]}</Typography>
              {remainingItems > 0 && renderMoreItems()}
            </Box>
          </Box>
        </Box>
        <Box style={[styles.col, isDesktop && styles.colDesktop]}>
          <Box style={styles.fullWidth}>
            <ActionButton
              testID="recent-order-view-details"
              variant={ActionButtonVariants.OUTLINE}
              fullWidth
              onPress={showOrderModal}
              aria-label={createAriaLabel({
                buttonType: 'view',
                order,
              })}
            >
              {t('viewDetails')}
            </ActionButton>
            <Box style={styles.orderBox}>
              <OrderAction
                order={order}
                loading={reorderedOrderId === order.rbiOrderId}
                disabled={isReorderingDisabled}
                onTrack={showTrackOrderModalPage}
                onReorder={handleReorderClick}
                onFinish={handleFinishOrder}
              />
            </Box>
          </Box>
        </Box>

        <ErrorDialog />
      </Box>
    </>
  );
};

export default Order;

const styles = StyleSheet.create({
  col: {
    width: '100%',
  },
  colDesktop: {
    width: '48%',
  },
  formattedDate: {
    marginBottom: spacing.$6,
    textTransform: getDesignToken('text-transform-headlines'),
  },
  fullWidth: {
    width: '100%',
  },
  itemNameContainer: {
    alignItems: 'flex-start',
    marginBottom: spacing.$6,
  },
  items: {
    paddingRight: spacing.$4,
  },
  moreItems: {
    paddingTop: spacing.$0_5,
  },
  moreItemsText: {
    color: getDesignToken('text-accent-order-number'),
  },
  numItems: {
    color: getDesignToken('text-default'),
    marginLeft: spacing.$1,
  },
  orderBox: {
    marginTop: spacing.$4,
    width: '100%',
  },
  orderNumber: {
    color: getDesignToken('text-accent-order-number'),
    fontSize: 16,
    margin: 0,
    paddingLeft: spacing.$1,
  },
  orderNumberText: {
    fontSize: 14,
    fontWeight: '400',
  },
  orderNumberWrapper: {
    flexDirection: 'row',
    marginHorizontal: 0,
    marginTop: spacing.$4,
  },
  subtitleRow: {
    flexDirection: 'row',
  },
  tile: {
    alignItems: 'center',
    backgroundColor: getDesignToken('background-pattern'),
    borderRadius: 16,
    justifyContent: 'center',
    marginVertical: spacing.$4,
    minHeight: spacing.$48,
    paddingBottom: spacing.$4,
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$1,
    ...shadows.$1,
  },
  tileDesktop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});
